import { useQuery } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '../../api/client';
import { usePrintHeader } from '../../hooks/usePrintHeader';
import { addressLine, date, fine, money, weight } from '../../utils/format';
import { ErrorNotice, Loading } from '../../components/ui';
import { PrintDocument, PrintToolbar } from '../../components/print/PrintDocument';

interface Line {
  line_no: number;
  item_code: string | null;
  product_name: string;
  hsn_code: string | null;
  purity_code: string | null;
  net_wt: string;
  fine_wt: string;
  taxable_value: string;
  cgst: string;
  sgst: string;
  igst: string;
  line_total: string;
  restock: boolean;
  condition: string | null;
}

interface CreditNote {
  doc_no: string;
  doc_date: string;
  sale_doc_no: string;
  sale_doc_date: string;
  reason: string;
  refund_mode: 'CASH' | 'BANK' | 'CREDIT' | 'EXCHANGE';
  taxable_value: string;
  tax_total: string;
  round_off: string;
  grand_total: string;
  customer_name: string | null;
  walkin_name: string | null;
  customer_phone: string | null;
  customer_address: unknown;
  customer_gstin: string | null;
  customer_pan: string | null;
  branch_name: string;
  place_of_supply: string | null;
  lines: Line[];
}

const REFUND: Record<CreditNote['refund_mode'], string> = {
  CASH: 'Refunded in cash',
  BANK: 'Refunded to the bank',
  CREDIT: 'Credited to the customer’s account',
  EXCHANGE: 'Set against a new purchase',
};

/**
 * The credit note for a sales return — the GST document that takes the sale
 * and its tax back. It names the invoice it reverses, so the two can be read
 * side by side.
 */
export function CreditNotePrintPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data, isLoading, error } = useQuery({
    queryKey: ['sales-return', id],
    queryFn: () => api.get<CreditNote>(`/sales-returns/${id}`),
  });
  const { header } = usePrintHeader(data?.branch_name);

  if (isLoading) return <Loading what="Preparing the credit note" />;
  if (error || !data) return <ErrorNotice error={error} />;

  const lines = data.lines ?? [];
  const sum = (k: 'cgst' | 'sgst' | 'igst'): number => lines.reduce((a, l) => a + Number(l[k] ?? 0), 0);
  const cgst = sum('cgst');
  const sgst = sum('sgst');
  const igst = sum('igst');

  return (
    <>
      <PrintToolbar onBack={() => navigate('/sales')} fileName={data.doc_no} />
      <PrintDocument
        title="Credit note"
        header={header}
        docNo={data.doc_no}
        docDate={date(data.doc_date)}
        party={{
          name: data.customer_name ?? data.walkin_name ?? 'Walk-in customer',
          phone: data.customer_phone,
          address: addressLine(data.customer_address) || null,
          gstin: data.customer_gstin,
          pan: data.customer_pan,
        }}
        partyLabel="Issued to"
        details={[
          { label: 'Against invoice', value: `${data.sale_doc_no} of ${date(data.sale_doc_date)}` },
          { label: 'Reason', value: data.reason },
          ...(data.place_of_supply ? [{ label: 'Place of supply', value: data.place_of_supply }] : []),
          { label: 'Pieces', value: String(lines.length) },
        ]}
        totals={[
          { label: 'Taxable value', value: money(data.taxable_value) },
          ...(cgst > 0 ? [{ label: 'CGST', value: money(cgst.toFixed(2)) }] : []),
          ...(sgst > 0 ? [{ label: 'SGST', value: money(sgst.toFixed(2)) }] : []),
          ...(igst > 0 ? [{ label: 'IGST', value: money(igst.toFixed(2)) }] : []),
          ...(Number(data.round_off) !== 0 ? [{ label: 'Round off', value: money(data.round_off) }] : []),
        ]}
        grandTotal={data.grand_total}
        grandTotalLabel="Credit total"
        paymentLabel="Refund"
        paymentMethod={REFUND[data.refund_mode]}
        footNote={`This credit note reverses the value and GST of the pieces listed from invoice ${data.sale_doc_no}.`}
      >
        <table className="doc-table">
          <thead>
            <tr>
              <th className="w-6">#</th>
              <th>Item</th>
              <th className="c">HSN</th>
              <th className="c">Purity</th>
              <th className="r">Net wt</th>
              <th className="r">Fine wt</th>
              <th className="r">Taxable</th>
              <th className="r">Amount</th>
            </tr>
          </thead>
          <tbody>
            {lines.map((l) => (
              <tr key={l.line_no}>
                <td className="num !text-left">{l.line_no}</td>
                <td>
                  <span className="font-medium">{l.product_name}</span>
                  {l.item_code ? (
                    <span className="block font-mono text-[11px] text-ink-soft">{l.item_code}</span>
                  ) : null}
                  {!l.restock ? (
                    <span className="block text-[11px] text-ink-soft">
                      Kept aside as damaged{l.condition ? ` — ${l.condition}` : ''}
                    </span>
                  ) : null}
                </td>
                <td className="c num">{l.hsn_code ?? '—'}</td>
                <td className="c num font-semibold">{l.purity_code ?? '—'}</td>
                <td className="num">{weight(l.net_wt)}</td>
                <td className="num">{fine(l.fine_wt)}</td>
                <td className="num">{money(l.taxable_value)}</td>
                <td className="num font-semibold">{money(l.line_total)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </PrintDocument>
    </>
  );
}
